import { Glue42Core } from "@glue42/core";
import { Glue42 } from "../../../../glue";
import { default as windowStore } from "../../store";
import { Environment, Executor, WindowsAPIConfiguration, WindowConstructorOptions, WindowEvents, WindowWrapper, GroupCreationArgs } from "../../types";
import windowFactory from "../../window";
import { default as executor } from "./executor";
import { CallbackRegistry, UnsubscribeFunction, default as CallbackFactory } from "callback-registry";
import {
    FullInfoEvent,
    StreamEvent,
    WindowInfo,
    GroupHeaderVisibilityChanged,
    UrlChanged,
    TitleChanged,
    BoundsChanged,
    FocusChanged,
    CompositionChanged,
    ExtendedStreamEvent,
    EventWithResponseData,
    ResponseTypes,
    IsStickyChanged,
    GroupVisibilityChanged,
} from "./types";
import { getWindowsByTabGroupId } from "../helpers";

export default class GDEnvironment implements Environment {

    private _registry: CallbackRegistry = CallbackFactory();
    private _agm: Glue42Core.AGM.API;
    private _logger: Glue42Core.Logger.API;
    private _waitTimeout = 10000;
    private _agmInstance: Glue42.Interop.Instance;
    private _windowId: string;
    private _configuration: WindowsAPIConfiguration;
    private _appManagerGetter: () => Glue42.AppManager.API;
    private _displayAPIGetter: () => Glue42.Displays.API;
    private _channelsAPIGetter: () => Glue42.Channels;

    constructor(agm: Glue42Core.AGM.API, logger: Glue42Core.Logger.API, appManagerGetter: () => Glue42.AppManager.API, displayAPIGetter: () => Glue42.Displays.API, channelsAPIGetter: () => Glue42.Channels, wndId?: string) {
        this._agm = agm;
        this._logger = logger.subLogger("gd-env");
        this._windowId = wndId;
        this._appManagerGetter = appManagerGetter;
        this._displayAPIGetter = displayAPIGetter;
        this._channelsAPIGetter = channelsAPIGetter;
    }

    public init(): Promise<GDEnvironment> {
        return new Promise((resolve, reject) => {
            this._agm.register("T42.Wnd.OnEventWithResponse", (args, caller) => {
                return this.respondToEvent(args);
            });

            const streamPromise = new Promise<void>((streamResolve, streamReject) => {
                this._agm.subscribe("T42.Wnd.OnEvent", {
                    waitTimeoutMs: this._waitTimeout,
                    target: "best",
                    arguments: {
                        withConfig: true
                    },
                    onData: (streamData: any) => {
                        if (streamData.data.type === "Configuration") {
                            this._configuration = streamData.data;
                            executor.setConfiguration(this._configuration);
                            return;
                        }
                        this.updateWindow(streamData.data, resolve);
                        executor.handleEvent(streamData.data);
                    },
                    onConnected: (instance) => {
                        this._agmInstance = instance;
                        // Init the executor
                        executor.init(this._agm, this._agmInstance);
                    }
                }).catch((error: { message: string, method: { name: string }, called_with: unknown }) => {
                    const message = `${error?.method?.name} - ${JSON.stringify(error?.called_with)} - ${error?.message}`;
                    reject(new Error(message));
                });
            });
        });
    }

    public get executor(): Executor {
        return executor;
    }

    public open(name: string, url: string, options: any): Promise<Glue42.Windows.GDWindow> {
        options = options || {};
        const copyOptions = { ...options };

        if (copyOptions.relativeTo !== undefined && typeof copyOptions.relativeTo !== "string") {
            copyOptions.relativeTo = copyOptions.relativeTo.id || "";
        }

        copyOptions.name = name;
        copyOptions.url = url;

        // This is for compatibility between gd2 and gd3
        copyOptions.windowState = options.windowState || options.state;
        // Otherwise glue desktop throws an error for extra properties
        delete copyOptions.state;
        return this.executor.open(copyOptions);
    }

    public createFlydown(windowId: string, options: Glue42.Windows.FlydownOptions): Promise<Glue42.Windows.Flydown> {
        return this.executor.createFlydown(windowId, options);
    }

    public async showPopup(windowId: string, options: Glue42.Windows.PopupOptions): Promise<void> {
        const window = windowStore.get(windowId);
        await this.executor.showPopup(window.API, options);
    }

    public tabAttached(callback: (tabWindow: Glue42.Windows.GDWindow, newTabGroupId: string, tabHeaderVisible: boolean) => void): UnsubscribeFunction {
        return this._registry.add("tab-attached", callback);
    }

    public tabDetached(callback: (window: Glue42.Windows.GDWindow, tabGroupId: string, oldTabGroupId: string) => void): UnsubscribeFunction {
        return this._registry.add("tab-detached", callback);
    }

    public onWindowFrameColorChanged(callback: (window: Glue42.Windows.GDWindow) => void) {
        return this._registry.add("frame-color-changed", callback);
    }

    public onEvent(callback: (windowInfo: any) => void): UnsubscribeFunction {
        return this._registry.add("window-event", callback);
    }

    public my(): string {
        return this._windowId;
    }

    public execute<T>(command: string, windowId: string, options: any): Promise<Glue42Core.AGM.InvocationResult<T>> {
        return this._agm.invoke<T>("T42.Wnd.Execute", {
            command,
            options,
            windowId,
        });
    }

    /** Groups */
    public onCompositionChanged(callback: (windowInfo: any) => void): UnsubscribeFunction {
        return this._registry.add("composition-changed", callback);
    }

    public onGroupHeaderVisibilityChanged(callback: (stream: any) => void): UnsubscribeFunction {
        return this._registry.add("group-header-changed", callback);
    }

    public onGroupVisibilityChanged(callback: (stream: any) => void): UnsubscribeFunction {
        return this._registry.add("group-visibility-changed", callback);
    }

    public onGroupStateChanged(callback: (stream: any) => void): UnsubscribeFunction {
        return this._registry.add("group-state-changed", callback);
    }

    public onWindowGotFocus(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return this._registry.add("got-focus", callback);
    }

    public onWindowLostFocus(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return this._registry.add("lost-focus", callback);
    }

    private respondToEvent(args: EventWithResponseData): Promise<ResponseTypes> {
        if (args.type === "ShowFlydownBoundsRequested") {
            return this.executor.handleFlydownBoundsRequested(args.data.windowId, args.data);
        } else if (args.type === "OnClosing" || args.type === "OnRefreshing" || args.type === "OnNavigating") {
            return this.executor.handleOnEventRequested(args.data.callbackId, args.data.args);
        }
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return Promise.reject(`There isn't a handler for ${(args as any).type}`);
    }

    private updateWindow(windowInfo: StreamEvent, readyResolve: (env: GDEnvironment) => void) {
        const extendedStreamEvent = this.getExtendedStreamEvent(windowInfo);

        // this is the snapshot of all windows
        if (windowInfo.type === "Snapshot") {
            const windowInfoFullInfoEvent = (windowInfo as FullInfoEvent);
            windowInfoFullInfoEvent.windows.forEach((w) => {
                const existingWindow = windowStore.get(w.id);
                if (existingWindow) {
                    existingWindow.Events.handleUpdate(this.mapToWindowConstructorOptions(w));
                    existingWindow.GroupCreationArgs = this.mapToGroupCreationArgs(w);
                } else {
                    const win = this.createWindow(w.id, w);
                    windowStore.markReadyToShow(win.API.id);
                }
                this._registry.execute("window-event", extendedStreamEvent);
            });
            readyResolve(this);
            return;
        }

        // global event
        if (windowInfo.type === "CommandExecuted") {
            this._registry.execute("window-event", extendedStreamEvent);
            return;
        }

        // if new window handle it separately
        if (windowInfo.type === "Created") {
            const windowInfoCreatedEvent = (windowInfo);
            const win = this.createWindow(windowInfoCreatedEvent.windowId, windowInfoCreatedEvent.data || {});
            windowStore.setReadyState(win.API.id);
            this._registry.execute("window-event", extendedStreamEvent);
            return;
        }

        if (windowInfo.type === "OnGroupVisibilityChanged") {
            const info = windowInfo as GroupVisibilityChanged;
            this._registry.execute("group-visibility-changed", info.data);
            this._registry.execute("window-event", windowInfo);
            return;
        }

        if (windowInfo.type === "OnGroupStateChanged") {
            const info = windowInfo as GroupVisibilityChanged;
            this._registry.execute("group-state-changed", info.data);
            this._registry.execute("window-event", windowInfo);
            return;
        }

        // otherwise check if the window is in our internal collections and update its state
        const windowObjectAndEvents: WindowWrapper = windowStore.get((windowInfo).windowId);
        if (!windowObjectAndEvents) {
            this._logger.error(`received update for unknown window. Stream:', ${JSON.stringify(windowInfo, null, 4)}`);
            return;
        }

        const theWindow: Glue42.Windows.GDWindow = windowObjectAndEvents.API;
        const theWindowEvents: WindowEvents = windowObjectAndEvents.Events;

        // handle bounds changed
        if (windowInfo.type === "BoundsChanged") {
            const windowInfoBoundsChangedEvent = (windowInfo as BoundsChanged);
            theWindowEvents.handleBoundsChanged(windowInfoBoundsChangedEvent.data);
        }

        // handle url changed
        if (windowInfo.type === "UrlChanged") {
            const windowInfoUrlChangedEvent = (windowInfo as UrlChanged);
            windowStore.setUrlChangedState(windowInfoUrlChangedEvent.windowId);
            theWindowEvents.handleUrlChanged(windowInfoUrlChangedEvent.data);
        }

        // handle title changed
        if (windowInfo.type === "TitleChanged") {
            const windowInfoTitleChanged = (windowInfo as TitleChanged);
            theWindowEvents.handleTitleChanged(windowInfoTitleChanged.data);
        }

        // handle isSticky changed
        if (windowInfo.type === "IsStickyChanged") {
            const windowInfoIsStickyChangedChanged = (windowInfo as IsStickyChanged);
            theWindowEvents.handleIsStickyChanged(windowInfoIsStickyChangedChanged.data);
        }

        // handle visibility changed
        if (windowInfo.type === "VisibilityChanged") {
            theWindowEvents.handleVisibilityChanged(windowInfo.data);
        }

        // handle context changed
        if (windowInfo.type === "ContextChanged") {
            theWindowEvents.handleContextUpdated(windowInfo.data);
        }

        // handle window state changed
        if (windowInfo.type === "StateChanged") {
            theWindowEvents.handleWindowChangeState(windowInfo.data);
        }

        // handle frame color changed
        if (windowInfo.type === "FrameColorChanged") {
            theWindowEvents.handleFrameColorChanged(windowInfo.data);
            this._registry.execute("frame-color-changed", theWindow);
        }

        // handle composition changed - groups API
        if (windowInfo.type === "CompositionChanged") {
            const windowInfoCompositionChanged = windowInfo as CompositionChanged;
            theWindowEvents.handleCompositionChanged(windowInfoCompositionChanged.data);
            windowStore.setCompositionChangedState(windowInfoCompositionChanged.data.windowId, this._configuration?.windowAvailableOnURLChanged);
            this._registry.execute("composition-changed", windowInfoCompositionChanged.data);
        }

        if (windowInfo.type === "GroupHeaderVisibilityChanged") {
            const info = windowInfo as GroupHeaderVisibilityChanged;
            theWindowEvents.handleGroupHeaderVisibilityChanged(info.data.groupHeaderVisible);
            this._registry.execute("group-header-changed", info.data);
        }

        // handle focus changed
        if (windowInfo.type === "FocusChanged") {
            const windowInfoFocusChanged = (windowInfo as FocusChanged);
            this.focusChanged(theWindowEvents, theWindow, windowInfoFocusChanged.data);
        }

        if (windowInfo.type === "WindowFrameChanged") {
            theWindowEvents.handleFrameAttached(windowInfo.data.frameId, windowInfo.data.frameId, windowInfo.data.isTabHeaderVisible);
            this._registry.execute("frame-changed");
        }

        if (windowInfo.type === "WindowFrameAdded") {
            const winsToBeNotified = getWindowsByTabGroupId(theWindow.id, windowInfo.data.frameId);
            const data = windowInfo.data;
            theWindowEvents.handleAttached(data.frameId, data.frameId, data.isTabHeaderVisible, data.isLocked, winsToBeNotified)
                .then(async () => {
                    // don't fire the event if it is the first window
                    if (winsToBeNotified.length > 0) {
                        await executor.finished;
                        this._registry.execute("tab-attached", theWindow, windowInfo.data.frameId, windowInfo.data.isTabHeaderVisible);
                    }
                });
        }

        if (windowInfo.type === "WindowFrameRemoved") {
            // In the event, tabGroupId is empty, will be update on the next event FrameAttached
            const oldTabGroupId = theWindow.tabGroupId;
            const winsToBeNotified = getWindowsByTabGroupId(theWindow.id, oldTabGroupId);
            theWindowEvents.handleDetached(windowInfo.data.isLocked, winsToBeNotified)
                .then(async () => {
                    // don't fire the event if it is the first window
                    if (winsToBeNotified.length > 0) {
                        await executor.finished;
                        this._registry.execute("tab-detached", theWindow, windowInfo.data.frameId, theWindow.tabGroupId);
                    }
                });
        }

        if (windowInfo.type === "TabHeaderVisibilityChanged") {
            theWindowEvents.handleTabHeaderVisibilityChanged(windowInfo.data.isTabHeaderVisible);
        }

        if (windowInfo.type === "FrameSelectionChanged") {
            theWindowEvents.handleFrameSelectionChanged(windowInfo.data.newWindowId, windowInfo.data.prevWindowId);
        }

        if (windowInfo.type === "ButtonClicked") {
            theWindowEvents.handleFrameButtonClicked(windowInfo.data);
        }

        if (windowInfo.type === "ButtonAdded") {
            theWindowEvents.handleFrameButtonAdded(windowInfo.data);
        }

        if (windowInfo.type === "ButtonRemoved") {
            theWindowEvents.handleFrameButtonRemoved(windowInfo.data);
        }

        if (windowInfo.type === "WindowZoomFactorChanged") {
            theWindowEvents.handleZoomFactorChanged(windowInfo.data);
        }

        // Clear the window on close event
        if (windowInfo.type === "Closed") {
            windowStore.remove(windowObjectAndEvents);
            theWindowEvents.handleWindowClose();
        }

        if (windowInfo.type === "FrameIsLockedChanged") {
            theWindowEvents.handleFrameIsLockedChanged(windowInfo.data);
        }

        if (windowInfo.type === "PlacementSettingsChanged") {
            theWindowEvents.handlePlacementSettingsChanged(windowInfo.data);
        }

        // expose to external listeners
        this._registry.execute("window-event", extendedStreamEvent);
    }

    private createWindow(windowId: string, options: WindowInfo): WindowWrapper {
        const windowObjAndEvents: WindowWrapper = windowFactory(windowId, this.mapToWindowConstructorOptions(options), executor, this._logger, this._appManagerGetter, this._displayAPIGetter, this._channelsAPIGetter, this._agm);
        windowObjAndEvents.GroupCreationArgs = this.mapToGroupCreationArgs(options);
        windowStore.add(windowObjAndEvents);
        return windowObjAndEvents;
    }

    private async focusChanged(theWindowEvents: WindowEvents, theWindow: Glue42.Windows.GDWindow, focus: boolean) {
        theWindowEvents.handleFocusChanged(focus);
        await windowStore.waitFor(theWindow.id);
        if (focus) {
            this._registry.execute("got-focus", theWindow);
        } else {
            this._registry.execute("lost-focus", theWindow);
        }
    }

    private mapToWindowConstructorOptions(args: WindowInfo): WindowConstructorOptions {
        // TODO
        return {
            name: args.name,
            context: args.context,
            bounds: args.bounds,
            url: args.url,
            title: args.title,
            isVisible: args.isVisible,
            focus: args.isFocused,
            state: args.state,
            frameColor: args.frameColor,
            groupId: args.groupId,
            neighbours: args.neighbors,
            isFocused: args.isFocused,
            isGroupHeaderVisible: args.groupHeaderVisible,
            isCollapsed: args.isCollapsed,
            tabGroupId: args.frameId,
            frameId: args.frameId,
            mode: args.mode,
            isTabHeaderVisible: args.isTabHeaderVisible,
            isTabSelected: args.isActiveTab,
            settings: args.settings,
            windowType: args.windowType,
            zoomFactor: args.zoomFactor,
            // focus: args.focus,
            isLocked: args.isLocked,
            placementSettings: args.placementSettings,
            isSticky: args.isSticky,
            tabIndex: args.tabIndex,
            // settings: windowStyleAttributes,
            frameButtons: args.frameButtons,
            jumpListOptions: args.jumpList,
            applicationName: args.applicationName
        };
    }

    private mapToGroupCreationArgs(args: WindowInfo): GroupCreationArgs {
        return {
            isGroupHibernated: args.isGroupHibernated,
            isGroupVisible: args.isGroupVisible
        };
    }

    private getExtendedStreamEvent(streamEvent: StreamEvent): StreamEvent | ExtendedStreamEvent {
        try {
            // some events (e.g. Snapshot) do not have windowIds
            if (!streamEvent.windowId) {
                return streamEvent;
            }

            // try to get the window, if not there return the original event
            const window = windowStore.get(streamEvent.windowId);
            if (!window) {
                return streamEvent;
            }

            const result: ExtendedStreamEvent = {
                state: streamEvent.type,
                windowName: window.API.name,
                ...streamEvent
            };

            if (result.state === "WindowFrameAdded") {
                result.state = "TabAttached";
            }

            if (result.state === "StateChanged") {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                result.state = result.data.charAt(0).toUpperCase() + result.data.slice(1);
            }

            if (result.state === "ButtonAdded") {
                result.state = "FrameButtonAdded";
            }

            if (result.state === "ButtonRemoved") {
                result.state = "FrameButtonRemoved";
            }

            return result;
        } catch (error) {
            return streamEvent;
        }
    }
}
